import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Presentation, Users, Lock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = ({ onLoginSuccess }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [error, setError] = useState('');
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      });

      // Store token and user info
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user_id', response.data.user_id);
      localStorage.setItem('username', response.data.username);

      // Call success handler
      onLoginSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setWaitlistSuccess(false);

    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        email: waitlistEmail
      });

      setWaitlistSuccess(true);
      setWaitlistEmail('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Presentation className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Speaker Notes
            </h1>
          </div>
          <Button onClick={() => setShowLogin(!showLogin)} variant="outline">
            {showLogin ? 'Hide Login' : 'Login'}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Elevate Your
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Presentations
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Build presentations enhanced with AI-powered depth notes, including scholarly references, citations, contextual data, and further reading—so your audience can go deeper than the slide.
          </p>
        </div>

        {/* Login and Waitlist Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="flex flex-col md:flex-row gap-8 justify-center items-start">
            {/* Login Card */}
            {showLogin && (
              <Card className="border-blue-200 shadow-lg w-full md:w-96">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-blue-600" />
                  <CardTitle>Login</CardTitle>
                </div>
                <CardDescription>
                  Access your Speaker Notes account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="username">Email</Label>
                    <Input
                      id="username"
                      type="email"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                      {error}
                    </div>
                  )}
                  
                  {/* Beta Agreement Notice */}
                  <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3">
                    By continuing, you agree to the Beta NDA & Confidentiality Agreement.{' '}
                    <a 
                      href="/beta-agreement" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline font-medium"
                    >
                      See Agreement and Terms here
                    </a>
                    .
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </CardContent>
              </Card>
            )}

            {/* Waitlist Card */}
            <Card className="border-purple-200 shadow-lg w-full md:w-96">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-purple-600" />
                <CardTitle>Join the Waitlist</CardTitle>
              </div>
              <CardDescription>
                Be the first to know when we launch
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleWaitlist} className="space-y-4">
                <div>
                  <Label htmlFor="waitlist-email">Email Address</Label>
                  <Input
                    id="waitlist-email"
                    type="email"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                {error && !showLogin && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                    {error}
                  </div>
                )}
                {waitlistSuccess && (
                  <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>You've been added to the waitlist! We'll be in touch soon.</span>
                  </div>
                )}
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" disabled={loading}>
                  {loading ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </form>
            </CardContent>
            </Card>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <Card className="border-slate-200">
            <CardHeader>
              <Presentation className="w-10 h-10 text-blue-600 mb-3" />
              <CardTitle className="text-lg">Rich Presentations</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Create beautiful slides with rich text formatting, custom backgrounds, and embedded media.
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <Users className="w-10 h-10 text-purple-600 mb-3" />
              <CardTitle className="text-lg">Live Collaboration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Present to audiences in real-time with synchronized slides and interactive features.
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CheckCircle className="w-10 h-10 text-green-600 mb-3" />
              <CardTitle className="text-lg">AI-Powered Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Generate comprehensive speaker notes automatically with AI to enhance your presentations.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white/80 backdrop-blur-sm mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-slate-600">
          <p>&copy; 2025 Speaker Notes. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
